// Practice: one skill at a time. Tabs follow the real sitting order; each tab
// is a calm list of that skill's task types, title, your numbers, quiet
// actions. One accent per row, reserved for the action that starts work.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, BookOpen, Headphones, Mic, Monitor, PenLine } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

const SECTION_ORDER = {
  celpip: ['listening', 'reading', 'writing', 'speaking'],
  pte: ['speaking', 'writing', 'reading', 'listening'],
};

const SECTION_META = {
  listening: { icon: Headphones, blurb: 'Audio plays once, exactly like test day.' },
  reading: { icon: BookOpen, blurb: 'Real passage lengths under the real clock.' },
  writing: { icon: PenLine, blurb: 'Hard word-count rules, enforced before any feedback.' },
  speaking: { icon: Mic, blurb: 'The mic opens on the exam’s clock. You record once.' },
};

export default function PracticeHub() {
  const { exam } = useParams();
  const [params, setParams] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [progress, setProgress] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [c, p] = await Promise.all([api.curriculum(exam), api.progress(exam)]);
        setUnits(c.data.units);
        setProgress(p.data.progress);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load practice.');
      }
    };
    load();
  }, [exam]);

  const sections = SECTION_ORDER[exam] || SECTION_ORDER.celpip;
  const active = params.get('skill') || sections[0];

  const bySection = useMemo(() => {
    const taskUnits = [...new Map(units.filter((u) => u.task_type).map((u) => [u.task_type, u])).values()];
    const map = {};
    taskUnits.forEach((u) => { (map[u.section] = map[u.section] || []).push(u); });
    Object.values(map).forEach((list) => list.sort((a, b) => a.order - b.order));
    return map;
  }, [units]);

  if (error) return <AppShell><p className="text-red-600">{error}</p></AppShell>;

  const statsFor = (tt) => progress.find((p) => p.task_type === tt);
  const rows = bySection[active] || [];
  const meta = SECTION_META[active] || {};

  const practicePath = (u) => `/${exam}/sets/${u.task_type}`;

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mono-stat">{exam} · timed practice</p>
          <h1 className="h-display mt-2 text-3xl" data-testid="practice-hub-hero">Train one task at a time.</h1>
        </div>
        <Link to={`/${exam}/exam-walkthrough`} data-testid="exam-walkthrough-btn"
          className="inline-flex shrink-0 items-center gap-2 border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900">
          <Monitor className="h-4 w-4" /> Walk through the exam
        </Link>
      </div>

      <div className="mt-8 flex border-b-2 border-slate-900" data-testid="practice-tabs">
        {sections.filter((s) => bySection[s]).map((section) => {
          const Icon = SECTION_META[section]?.icon;
          return (
            <button
              key={section}
              type="button"
              data-testid={`practice-tab-${section}`}
              onClick={() => setParams({ skill: section }, { replace: true })}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-semibold capitalize transition-colors',
                section === active ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-900'
              )}
            >
              {Icon && <Icon className="h-4 w-4" />} {section}
            </button>
          );
        })}
      </div>

      <div className="flex items-baseline justify-between py-4">
        <p className="text-sm text-slate-500">{meta.blurb}</p>
        <span className="text-xs text-slate-400">{rows.length} task types</span>
      </div>

      <div className="border border-slate-200 bg-white">
        {rows.map((u, i) => {
          const stats = statsFor(u.task_type);
          const pct = stats?.accuracy != null ? Math.round(stats.accuracy * 100) : null;
          return (
            <div
              key={u.task_type}
              className={cn('group flex items-center gap-6 px-6 py-5', i > 0 && 'border-t border-slate-100')}
            >
              <span className="w-7 shrink-0 text-xs font-medium tabular-nums text-slate-300">{String(i + 1).padStart(2, '0')}</span>
              <div className="min-w-0 flex-1">
                <Link
                  to={practicePath(u)}
                  data-testid={`practice-type-${u.task_type}`}
                  className="font-display text-[16px] font-semibold text-slate-900 hover:text-indigo-700"
                >
                  {u.title}
                </Link>
                <p className="mt-0.5 text-xs text-slate-400">
                  {stats ? `${stats.attempts} set${stats.attempts === 1 ? '' : 's'} completed` : 'Not attempted yet'}
                </p>
              </div>
              {pct != null && (
                <span className={cn('h-display w-14 shrink-0 text-right text-xl tabular-nums', pct >= 75 ? 'text-emerald-600' : 'text-indigo-600')}>
                  {pct}%
                </span>
              )}
              <div className="flex shrink-0 items-center gap-5">
                <Link to={`/${exam}/lessons/${u.unit_id}`} className="text-sm font-medium text-slate-400 hover:text-slate-900" data-testid={`hub-lesson-${u.task_type}`}>
                  Strategy
                </Link>
                {(u.section === 'writing' || u.section === 'speaking') && (
                  <Link to={`/${exam}/models/${u.task_type}`} className="text-sm font-medium text-slate-400 hover:text-slate-900" data-testid={`hub-models-${u.task_type}`}>
                    Models
                  </Link>
                )}
                <Link
                  to={practicePath(u)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  Start <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <p className="px-6 py-8 text-sm text-slate-400">Content arrives here as it is authored.</p>}
      </div>
    </AppShell>
  );
}
